import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';
import { useAPI } from './APIContext';
import { useAnalytics } from '../hooks/useAnalytics';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Total tutorial steps (must match Tutorial.js cases 1–16). No auto-advance, no DOM detection.
const totalTutorialSteps = 16;

const stepNames = [
  '',
  'welcome',
  'storyverse',
  'first_character',
  'create_button',
  'create_character',
  'draw',
  'drawing_tools',
  'drawing_tools_multi',
  'draw_create',
  'creating',
  'save_character',
  'pixley_stars',
  'create_story',
  'storybooks_intro',
  'storybook_card',
  'open_storybook',
];

export const TutorialProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { apiClient } = useAPI();
  const { trackTutorialStep, setUserProperties } = useAnalytics();

  // --- State (mirrors OnboardingContext) ---
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem('pixley_tutorial_step');
    const parsed = saved ? parseInt(saved, 10) : 1;
    if (parsed < 1 || parsed > totalTutorialSteps || isNaN(parsed)) {
      localStorage.removeItem('pixley_tutorial_step');
      localStorage.removeItem('pixley_tutorial_active');
      return 1;
    }
    return parsed;
  });
  const [tutorialCompleted, setTutorialCompleted] = useState(() => {
    return localStorage.getItem('pixley_tutorial_completed') === 'true';
  });

  // Analytics tracking state (same pattern as OnboardingContext)
  const [tutorialStartTime, setTutorialStartTime] = useState(null);
  const [stepStartTime, setStepStartTime] = useState(null);
  const [stepsCompleted, setStepsCompleted] = useState({});

  // Product tour is fully disabled (2026-08-17): the reworked onboarding +
  // first-story handoff replaced it. Flip to true to bring it back.
  const TUTORIAL_ENABLED = false;

  // --- When to show tutorial (no polling): either just finished onboarding, or resuming mid-flow ---
  useEffect(() => {
    if (!TUTORIAL_ENABLED) {
      // Clear any stale flags so nothing resumes if it's ever re-enabled
      localStorage.removeItem('pixley_onboarding_just_completed');
      localStorage.removeItem('pixley_tutorial_active');
      setShowTutorial(false);
      return;
    }
    if (!isAuthenticated || !user) return;

    const hasCompletedTutorial = localStorage.getItem('pixley_tutorial_completed') === 'true';
    const tutorialActive = localStorage.getItem('pixley_tutorial_active') === 'true';
    const onboardingJustCompleted = localStorage.getItem('pixley_onboarding_just_completed') === 'true';

    if (tutorialActive) {
      // Resume: user left mid-tutorial (e.g. refresh). Restore and show.
      logger.log('🎓 Tutorial already in progress, resuming at step:', currentStep);
      setShowTutorial(true);
      trackTutorialStep('tutorial', 'resumed', {
        resumed_at_step: currentStep,
        total_steps: totalTutorialSteps,
      });
      return;
    }

    if (onboardingJustCompleted && !hasCompletedTutorial) {
      // Fresh start: onboarding just finished → start tutorial at step 1.
      logger.log('🎓 Starting tutorial for new user');
      localStorage.removeItem('pixley_onboarding_just_completed');
      setCurrentStep(1);
      setShowTutorial(true);
      setStepsCompleted({});
      const startTime = Date.now();
      setTutorialStartTime(startTime);
      setStepStartTime(startTime);
      localStorage.setItem('pixley_tutorial_active', 'true');
      localStorage.setItem('pixley_tutorial_step', '1');
      trackTutorialStep('tutorial', 'started', {
        total_steps: totalTutorialSteps,
        user_id: user?.id,
      });
      if (stepNames[1]) {
        trackTutorialStep(stepNames[1], 'started', {
          step_number: 1,
          total_steps: totalTutorialSteps,
        });
      }
      saveTutorialAnalytics({
        status: 'in_progress',
        started_at: new Date(startTime).toISOString(),
        current_step: 1,
        total_steps: totalTutorialSteps,
        stopped_at_step: null,
        stopped_at_step_number: null,
        steps_completed: {},
      }).catch(() => {});
    }
  }, [isAuthenticated, user, trackTutorialStep]);

  // Save in_progress to backend when current step changes (steps 1–16 only, while tutorial visible).
  // Do NOT run when showTutorial is false so we never overwrite completed/skipped with in_progress.
  useEffect(() => {
    if (currentStep < 1 || currentStep > totalTutorialSteps || !showTutorial) return;
    if (!user?.id || !apiClient) return;
    saveTutorialAnalytics({
      status: 'in_progress',
      started_at: tutorialStartTime ? new Date(tutorialStartTime).toISOString() : undefined,
      current_step: currentStep,
      total_steps: totalTutorialSteps,
      completion_percentage: Math.round((currentStep / totalTutorialSteps) * 100),
      stopped_at_step: null,
      stopped_at_step_number: null,
      steps_completed: stepsCompleted,
    });
  }, [currentStep, showTutorial, tutorialStartTime, stepsCompleted]);

  // --- Save tutorial analytics to backend (same pattern as OnboardingContext.saveOnboardingAnalytics) ---
  function saveTutorialAnalytics(payload) {
    if (!user?.id || !apiClient) {
      logger.warn('⚠️ Cannot save tutorial analytics: missing user or API client');
      return Promise.resolve();
    }
    return apiClient
      .updateUserAnalytics(user.id, payload, 'tutorial')
      .then((response) => {
        if (response?.status === 'success' || response?.success) {
          logger.log('✅ Tutorial analytics saved');
        } else {
          logger.warn('⚠️ Tutorial analytics save failed:', response?.error || response?.message);
        }
      })
      .catch((err) => {
        logger.error('❌ Error saving tutorial analytics:', err);
      });
  }

  // --- Next: only when user clicks Next. Mark previous step completed, save time spent, move to next step. ---
  const nextStep = () => {
    if (currentStep >= totalTutorialSteps) return;
    const newStep = currentStep + 1;
    const timeSpentOnStep = stepStartTime
      ? Math.round((Date.now() - stepStartTime) / 1000)
      : null;

    logger.log('➡️ Tutorial nextStep:', {
      currentStep,
      newStep,
      totalTutorialSteps,
      timeSpentOnStep,
    });

    if (stepNames[currentStep]) {
      trackTutorialStep(stepNames[currentStep], 'completed', {
        time_spent_seconds: timeSpentOnStep,
        step_number: currentStep,
        total_steps: totalTutorialSteps,
      });
    }
    if (stepNames[newStep]) {
      trackTutorialStep(stepNames[newStep], 'started', {
        step_number: newStep,
        total_steps: totalTutorialSteps,
      });
    }

    const newStepsCompleted = {
      ...stepsCompleted,
      [currentStep]: { completed: true, time_spent_seconds: timeSpentOnStep },
    };
    setStepsCompleted(newStepsCompleted);
    setStepStartTime(Date.now());
    setCurrentStep(newStep);
    localStorage.setItem('pixley_tutorial_step', String(newStep));
    localStorage.setItem('pixley_tutorial_active', 'true');
    // in_progress for newStep is saved by the effect when currentStep/stepsCompleted update
  };

  // --- Previous: only when user clicks Back ---
  const prevStep = () => {
    if (currentStep <= 1) return;
    const newStep = currentStep - 1;
    setCurrentStep(newStep);
    localStorage.setItem('pixley_tutorial_step', String(newStep));
    localStorage.setItem('pixley_tutorial_active', 'true');
  };

  // --- Skip: ONLY when user clicks "Skip Tutorial". Never called automatically or on unmount. ---
  const skipTutorial = () => {
    const stepWhenSkipped = currentStep;
    const totalTimeSeconds = tutorialStartTime
      ? Math.round((Date.now() - tutorialStartTime) / 1000)
      : null;

    logger.log('⏭️ Tutorial skipped at step', stepWhenSkipped);

    trackTutorialStep('tutorial', 'skipped', {
      skipped_at_step: stepWhenSkipped,
      step_name: stepNames[stepWhenSkipped] || 'unknown',
      total_steps: totalTutorialSteps,
      completion_percentage: Math.round((stepWhenSkipped / totalTutorialSteps) * 100),
    });

    saveTutorialAnalytics({
      status: 'skipped',
      started_at: tutorialStartTime
        ? new Date(tutorialStartTime).toISOString()
        : undefined,
      current_step: stepWhenSkipped,
      stopped_at_step: stepNames[stepWhenSkipped] || 'unknown',
      stopped_at_step_number: stepWhenSkipped,
      steps_completed: stepsCompleted,
      total_time_seconds: totalTimeSeconds,
      completion_percentage: Math.round((stepWhenSkipped / totalTutorialSteps) * 100),
    });

    localStorage.removeItem('pixley_tutorial_step');
    localStorage.removeItem('pixley_tutorial_active');
    localStorage.setItem('pixley_tutorial_completed', 'true');
    setShowTutorial(false);
    setCurrentStep(1);
    setTutorialCompleted(true);
    setTutorialStartTime(null);
    setStepStartTime(null);
    setStepsCompleted({});
  };

  // --- Complete: when user clicks Finish on last step. Not auto-called. ---
  const completeTutorial = () => {
    logger.log('✅ Tutorial completed');

    const totalTimeSeconds = tutorialStartTime
      ? Math.round((Date.now() - tutorialStartTime) / 1000)
      : null;

    if (stepNames[currentStep]) {
      trackTutorialStep(stepNames[currentStep], 'completed', {
        step_number: currentStep,
        total_steps: totalTutorialSteps,
      });
    }
    trackTutorialStep('tutorial', 'completed', { total_steps: totalTutorialSteps });

    // Enrich PostHog person properties on tutorial completion
    setUserProperties({ tutorial_completed: true });

    const finalStepsCompleted = {
      ...stepsCompleted,
      [currentStep]: {
        completed: true,
        time_spent_seconds: stepStartTime
          ? Math.round((Date.now() - stepStartTime) / 1000)
          : null,
      },
    };

    saveTutorialAnalytics({
      status: 'completed',
      started_at: tutorialStartTime
        ? new Date(tutorialStartTime).toISOString()
        : undefined,
      completed_at: new Date().toISOString(),
      current_step: totalTutorialSteps,
      steps_completed: finalStepsCompleted,
      stopped_at_step: null,
      stopped_at_step_number: null,
      total_time_seconds: totalTimeSeconds,
    });

    localStorage.removeItem('pixley_tutorial_step');
    localStorage.removeItem('pixley_tutorial_active');
    localStorage.setItem('pixley_tutorial_completed', 'true');
    setShowTutorial(false);
    setCurrentStep(1);
    setTutorialCompleted(true);
    setTutorialStartTime(null);
    setStepStartTime(null);
    setStepsCompleted({});
  };

  // --- Start tutorial (e.g. from Settings / Help). Sets active + step 1, initializes analytics state. ---
  const startTutorial = () => {
    logger.log('🎓 Tutorial started manually');
    setCurrentStep(1);
    setShowTutorial(true);
    setTutorialCompleted(false);
    setStepsCompleted({});
    const startTime = Date.now();
    setTutorialStartTime(startTime);
    setStepStartTime(startTime);
    localStorage.setItem('pixley_tutorial_step', '1');
    localStorage.setItem('pixley_tutorial_active', 'true');
    localStorage.removeItem('pixley_tutorial_completed');
    trackTutorialStep('tutorial', 'started', { total_steps: totalTutorialSteps });
    if (stepNames[1]) {
      trackTutorialStep(stepNames[1], 'started', {
        step_number: 1,
        total_steps: totalTutorialSteps,
      });
    }
    saveTutorialAnalytics({
      status: 'in_progress',
      started_at: new Date(startTime).toISOString(),
      current_step: 1,
      total_steps: totalTutorialSteps,
      stopped_at_step: null,
      stopped_at_step_number: null,
      steps_completed: {},
    }).catch(() => {});
  };

  const resetTutorial = () => {
    logger.log('🔄 Tutorial reset');
    localStorage.removeItem('pixley_tutorial_step');
    localStorage.removeItem('pixley_tutorial_active');
    localStorage.removeItem('pixley_tutorial_completed');
    setTutorialCompleted(false);
    setShowTutorial(false);
    setCurrentStep(1);
    setTutorialStartTime(null);
    setStepStartTime(null);
    setStepsCompleted({});
  };

  const value = {
    showTutorial,
    currentStep,
    currentTutorialStep: currentStep,
    tutorialCompleted,
    totalSteps: totalTutorialSteps,
    totalTutorialSteps,
    isLastStep: currentStep === totalTutorialSteps,
    isLastTutorialStep: currentStep === totalTutorialSteps,
    isFirstStep: currentStep === 1,
    progress: totalTutorialSteps > 0 ? (currentStep / totalTutorialSteps) * 100 : 0,
    tutorialStartTime,
    stepStartTime,
    stepsCompleted,
    nextStep,
    nextTutorialStep: nextStep,
    prevStep,
    prevTutorialStep: prevStep,
    skipTutorial,
    completeTutorial,
    startTutorial,
    resetTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
